import React, { type PropsWithChildren, type ReactNode } from 'react';
import { Box, Grid, xcss } from '@atlaskit/primitives';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import {
	RightPanelContainer,
	useRightPanelState,
} from '../../controllers/right-panel-layout-controller.tsx';

type RightPanelLayoutContainerProps = {
	isOpenByDefault?: boolean;
	defaultPanelId?: string;
	children: ReactNode;
	rightContent: ReactNode;
};

const RIGHT_PANEL_WIDTH = gridSize * 46;

export const RightPanelLayoutContainer = ({
	isOpenByDefault = false,
	defaultPanelId,
	rightContent,
	children,
}: RightPanelLayoutContainerProps) => (
	<RightPanelContainer isOpenByDefault={isOpenByDefault} defaultPanelId={defaultPanelId} isGlobal>
		<Panels rightContent={rightContent}>{children}</Panels>
	</RightPanelContainer>
);

// `useRightPanelState` needs to be called from inside `RightPanelContainer` to ensure the value of isOpen is
// set correctly on initial render (otherwise it defaults to false), so we need a separate component here.
const Panels = ({
	rightContent,
	children,
}: PropsWithChildren<Pick<RightPanelLayoutContainerProps, 'rightContent'>>) => {
	const { isOpen: isRightPanelOpen } = useRightPanelState();

	return (
		<Grid
			templateColumns={isRightPanelOpen ? `1fr ${RIGHT_PANEL_WIDTH}px` : '1fr 0'}
			gap={isRightPanelOpen ? 'space.500' : 'space.0'}
			xcss={gridStyles}
		>
			<Box xcss={leftPanelStyles}>{children}</Box>
			<Box as="aside" xcss={rightPanelStyles}>
				{rightContent}
			</Box>
		</Grid>
	);
};

const rightPanelStyles = xcss({
	position: 'sticky',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	top: `calc(${PAGE_LAYOUT_OFFSET_TOP})`,
	alignSelf: 'start',
	marginRight: 'space.negative.400',
});

const leftPanelStyles = xcss({
	minWidth: '0',
});

const gridStyles = xcss({
	transition: 'grid-template-columns 300ms cubic-bezier(0.2, 0, 0, 1) 0s',
});
